import { useState } from "react";
import { ArrowLeft, Pencil, RotateCcw } from "lucide-react";
import {
  Alert,
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  OwnerChip,
  Skeleton,
  initialsOf,
  toast,
} from "@playbookai/ui";
import { PlaybookApiError } from "@playbookai/api-clients";
import { useKpi } from "../hooks/useKpi.js";
import { useDefinitionNames } from "../hooks/useDefinitionNames.js";
import { useOwnerNames } from "../hooks/useOwnerNames.js";
import { useActivateKpi } from "../hooks/useActivateKpi.js";
import { useArchiveKpi } from "../hooks/useArchiveKpi.js";
import { useRestoreKpi } from "../hooks/useRestoreKpi.js";
import { useKpiCategoryOptions } from "../hooks/useKpiCategoryOptions.js";
import { useKpiDefinitions } from "../hooks/useKpiDefinitions.js";
import { useKpiActorUserId } from "../hooks/kpiAuthScope.js";
import { KpiLifecyclePill, KpiStatusBadges } from "./KpiStatusBadges.js";
import { KpiReadinessPill } from "./KpiReadinessPill.js";
import { AssignOwnerModal } from "./AssignOwnerModal.js";
import { KpiTargetSection } from "./KpiTargetSection.js";
import { KpiPerformanceSection } from "./KpiPerformanceSection.js";
import { KpiActivitySection } from "./KpiActivitySection.js";
import { KpiConnectedWork } from "./KpiConnectedWork.js";
import { KpiPanel } from "./KpiPanel.js";
import type { ManagedKpiObservationRange } from "../hooks/useKpiObservations.js";
import {
  DIRECTION_LABEL,
  LIFECYCLE_LABEL,
  READINESS_LABEL,
  REPORTING_FREQUENCY_LABEL,
  formatDateTime,
} from "./kpi-presentation.js";
import { errorMessage } from "./error-message.js";

export interface KpiDetailPageProps {
  readonly kpiId: string;
  /** Back to All KPIs. The feature owns no router (ADR-0009) — the host
   * navigates, which is what lets browser Back restore the collection's search,
   * filters, layout and loaded pages. */
  readonly onBack: () => void;
  /** Tenant-admin — show the governance actions. The server is still the
   * authoritative gate; this only hides UI. */
  readonly canManage: boolean;
  /** Open this KPI's edit page. Optional so a host that has not wired the route
   * simply renders no Edit button rather than one that goes nowhere. */
  readonly onEdit?: (() => void) | undefined;
  /** Open the Target-to-Improve page for this KPI's target (PB-5317). */
  readonly onOpenTti?: ((ttiId: string) => void) | undefined;
}

function Field({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="mb-0.75 text-dense-1 font-semibold uppercase tracking-wider text-pb-text-muted">
        {label}
      </dt>
      <dd className="break-words text-pb-text">{children}</dd>
    </div>
  );
}

const NONE = <span className="text-pb-text-muted">—</span>;

/**
 * The routed KPI detail page (`/settings/kpi/:kpiId`) — the drawer's successor.
 *
 * WHY A PAGE. The drawer had to hold a KPI's identity, its three status
 * dimensions, its target and its lifecycle actions in a column beside the list,
 * and the reading history and chart this ticket adds do not fit beside anything.
 * A route also makes the KPI addressable: a link in a review, a refresh, and
 * browser Back all behave the way a reader expects, none of which a piece of
 * component state could do.
 *
 * ONE SCROLLING PAGE, deliberately no internal tabs. There are three things to
 * know about a KPI — what it is, how it is doing, and what may be done to it —
 * and tabs would hide two of them behind a click while making the page's own
 * scroll position meaningless.
 *
 * THE ORDER IS THE ARGUMENT: Target to Improve (what this KPI is aiming at, and
 * where it stands), then Performance (the series behind that), then KPI details
 * (the quieter metadata). Each fact is stated ONCE — most visibly the lifecycle
 * status, which lives in the header pill and nowhere else, so an active KPI
 * whose readiness is also "Active" cannot render the same word twice.
 *
 * EACH ACTION SITS WHERE ITS SUBJECT IS. The header carries what acts on the
 * KPI as a whole — edit its metadata, move its ownership, activate, archive,
 * restore —
 * while `Set target` / `Edit target` lives in the Target to Improve card and the
 * two value affordances live beside the current value they change. An earlier
 * revision had a second `Set target` in the header as well; two copies of one
 * action in two places teaches a reader that neither is authoritative.
 *
 * Everything on it is what the KPI contract actually returns. There is no
 * "created by" line, because `GET /v1/kpis/:id` does not carry one: the
 * governance WRITE response has `createdByUserId` and the read does not, and
 * inventing a plausible attribution on a governance surface is worse than
 * omitting it.
 */
export function KpiDetailPage({
  kpiId,
  onBack,
  canManage,
  onEdit,
  onOpenTti,
}: KpiDetailPageProps): JSX.Element {
  const query = useKpi(kpiId);
  const activate = useActivateKpi();
  const archive = useArchiveKpi();
  const restore = useRestoreKpi();
  const actorUserId = useKpiActorUserId();
  const [assignOpen, setAssignOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  // The Performance history window — PAGE state, handed to the section as
  // props. The section stays mounted while `kpiId` changes underneath this
  // page, so state held inside it silently carried one KPI's chosen window
  // onto the next; only the owner of `kpiId` can scope it. Reset during
  // render (the adjust-on-prop-change pattern), not in an effect, so no render
  // pairs the new KPI with the old KPI's window.
  const [range, setRange] = useState<ManagedKpiObservationRange>("90d");
  const [rangeKpiId, setRangeKpiId] = useState(kpiId);
  if (rangeKpiId !== kpiId) {
    setRangeKpiId(kpiId);
    setRange("90d");
  }

  const kpi = query.data;
  const ownerNamesQuery = useOwnerNames([kpi?.ownerUserId ?? null]);
  // Resolves Type names including the compatibility/deprecated Types the catalog
  // list hides (B-1) — a backfilled KPI must not render its Type as "—".
  const definitionName = useDefinitionNames([kpi?.definitionId ?? null]);
  const definitions = useKpiDefinitions();
  // The same cached option union the Create page reads, so a category shows its
  // display name here and its bare code only when no installed pack describes it.
  const categoryOptions = useKpiCategoryOptions();
  const owner = kpi?.ownerUserId
    ? ownerNamesQuery.data?.[kpi.ownerUserId]
    : undefined;

  const activateReason = !kpi
    ? null
    : !kpi.ownerUserId
      ? "Assign an owner to activate"
      : !kpi.currentTarget
        ? "Set a target to activate"
        : null;

  const handleActivate = () => {
    if (!kpi) return;
    activate.mutate(kpi.kpiId, {
      onSuccess: () => toast("KPI activated", { tone: "healthy" }),
      onError: (err) =>
        toast(errorMessage(err, "Could not activate the KPI."), {
          tone: "critical",
        }),
    });
  };

  const handleArchive = () => {
    if (!kpi) return;
    archive.mutate(kpi.kpiId, {
      onSuccess: () => toast("KPI archived", { tone: "neutral" }),
      onError: (err) =>
        toast(errorMessage(err, "Could not archive the KPI."), {
          tone: "critical",
        }),
    });
  };

  const handleRestore = () => {
    if (!kpi) return;
    restore.mutate(kpi.kpiId, {
      onSuccess: () => {
        setRestoreOpen(false);
        toast("KPI restored to draft", { tone: "healthy" });
      },
      onError: (err) =>
        toast(errorMessage(err, "Could not restore the KPI."), {
          tone: "critical",
        }),
    });
  };

  const backLink = (
    <Button variant="ghost" size="sm" onClick={onBack} className="-ms-2 mb-3.5">
      <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
      All KPIs
    </Button>
  );

  if (query.isLoading) {
    return (
      <div className="space-y-5" aria-busy="true">
        {backLink}
        <Skeleton className="h-8 w-1/2" />
        {/* AP-24: rounded-lg overrides → shape="panel" (no picture change). */}
        <Skeleton className="h-24 w-full" shape="panel" />
        <Skeleton className="h-72 w-full" shape="panel" />
      </div>
    );
  }

  if (query.isError || !kpi) {
    // A 404 and a dropped connection are different facts, and only one of them
    // is about this KPI. Telling someone their KPI does not exist because the
    // network blipped is both wrong and unhelpful — it offers Back as the only
    // way out of a state a retry would clear, and it teaches them the KPI is
    // gone. `PlaybookApiError.status` is what tells them apart; anything that is
    // not a 404 (a 5xx, a timeout, an offline fetch that never became a
    // response) is transient until proven otherwise.
    //
    // A 400 is the same kind of answer: the route's ParseUUIDPipe refuses a
    // malformed id before any lookup, so a bad link landed on "Couldn’t load
    // this KPI" with a Try again that could never succeed (PB-5464 — the TTI
    // page had the same bug).
    const notFound =
      query.error instanceof PlaybookApiError &&
      (query.error.status === 404 || query.error.status === 400);
    return (
      <div className="space-y-5">
        {backLink}
        {notFound ? (
          <EmptyState
            variant="error"
            title="KPI not found"
            description="This KPI could not be loaded. It may have been removed, or the link may point at a KPI in another workspace."
            action={<Button onClick={onBack}>Back to All KPIs</Button>}
          />
        ) : (
          <EmptyState
            variant="error"
            title="Couldn’t load this KPI"
            description="We couldn’t load this KPI. Check your access or connection and try again."
            action={
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button onClick={() => void query.refetch()}>Try again</Button>
                <Button variant="secondary" onClick={onBack}>
                  Back to All KPIs
                </Button>
              </div>
            }
          />
        )}
      </div>
    );
  }

  const categoryLabel = kpi.categoryCode
    ? (categoryOptions.data?.find((o) => o.code === kpi.categoryCode)?.name ??
      kpi.categoryCode)
    : null;

  // Two pills reading the same word are not two signals. Readiness `active`
  // renders "Active" exactly as lifecycle `active` does, so the header keeps the
  // lifecycle pill — the KPI's governed headline — and drops the echo. Every
  // other readiness (Data Product Required, Stale, Unavailable, Inactive) says
  // something the lifecycle pill cannot, and is still shown. Compared on the
  // rendered LABEL rather than on the enum pair, so a future label change cannot
  // reintroduce the duplicate behind a passing test.
  const readinessAddsInfo =
    READINESS_LABEL[kpi.readiness] !== LIFECYCLE_LABEL[kpi.lifecycleStatus];

  // A null actor (auth still bootstrapping, or a host that wires no scope) is
  // "not the owner" — a permissive default would flash a control the server then
  // refuses. An archived KPI records nothing further, matching the 422. Derived
  // once here because both sections below need the answer: one to offer the
  // button, the other to word its empty chart.
  const canRecord =
    actorUserId !== null &&
    kpi.ownerUserId !== null &&
    actorUserId === kpi.ownerUserId &&
    kpi.lifecycleStatus !== "archived";

  // The template line under the name, as the canvas words it: the template's
  // name, its provenance and the version this KPI was created from.
  const definition = kpi.definitionId
    ? definitions.data?.find((d) => d.definitionId === kpi.definitionId)
    : undefined;
  const provenanceLabel = definition
    ? definition.provenance === "system"
      ? "System"
      : "Custom"
    : null;
  const templateLabel = kpi.definitionId
    ? `${definitionName(kpi.definitionId)}${provenanceLabel ? ` (${provenanceLabel})` : ""}`
    : "No template";
  const templateLine = kpi.definitionId
    ? `${definitionName(kpi.definitionId)} · ${provenanceLabel ? `${provenanceLabel} template` : "template"}${definition ? ` (v${definition.version})` : ""}`
    : "Custom KPI — no template";

  return (
    <div>
      {backLink}

      <header className="mb-4.5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-heading-1 tracking-tight text-pb-text">
              {kpi.name}
            </h1>
            <p className="mt-1 text-body-small text-pb-text-muted">
              {templateLine}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <KpiLifecyclePill status={kpi.lifecycleStatus} />
            {readinessAddsInfo ? (
              <KpiReadinessPill readiness={kpi.readiness} />
            ) : null}
          </div>
        </div>

        {!canManage && !canRecord ? (
          <Alert tone="info">
            Read-only. Contact a Transformation Lead for changes to this KPI.
          </Alert>
        ) : null}
        {kpi.lifecycleStatus === "archived" ? (
          <Alert tone="watch">This KPI is archived and kept for history.</Alert>
        ) : null}

        {canManage ? (
          <div className="flex flex-wrap items-center gap-2">
            {/* A named secondary action, not a naked pencil. The header carries
                four other verbs, and an unlabelled icon among them would be a
                guess — the icon is there to make the labelled button findable,
                not to replace the label. */}
            {onEdit ? (
              <Button variant="secondary" onClick={onEdit}>
                <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                Edit KPI
              </Button>
            ) : null}
            <Button variant="secondary" onClick={() => setAssignOpen(true)}>
              {kpi.ownerUserId ? "Reassign owner" : "Assign owner"}
            </Button>
            {/* `Set target` deliberately does NOT live here any more. It moved
                into the Target to Improve card, where the target it sets is on
                screen and where it can say "Edit target" once one exists — two
                copies of one action in two places is how a page teaches that
                neither is authoritative. */}
            {kpi.lifecycleStatus === "draft" ? (
              <Button
                variant="primary"
                onClick={handleActivate}
                disabled={Boolean(activateReason)}
                loading={activate.isPending}
              >
                Activate
              </Button>
            ) : null}
            {kpi.lifecycleStatus === "archived" ? (
              <Button
                variant="primary"
                onClick={() => setRestoreOpen(true)}
                loading={restore.isPending}
              >
                <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                Restore
              </Button>
            ) : null}
            {kpi.lifecycleStatus !== "archived" ? (
              <Button
                variant="destructive"
                onClick={handleArchive}
                loading={archive.isPending}
              >
                Archive
              </Button>
            ) : null}
            {/* Visible reason, never a tooltip: a disabled control does not
                reliably fire hover, so a tooltip hides the explanation from
                pointer and AT users alike. */}
            {kpi.lifecycleStatus === "draft" && activateReason ? (
              <span className="text-caption text-pb-text-muted">
                {activateReason}
              </span>
            ) : null}
          </div>
        ) : null}
      </header>

      <div className="space-y-3.5">
        {/* The OWNER may set the target too, not only a governor: `setTarget`
            checks ownership inside the service, because it is a row the
            controller has not read. Hiding the action from them left an API
            permission with no way to reach it. Archived is excluded for the
            same reason `canRecord` excludes it — the server answers 422. */}
        <KpiTargetSection
          kpi={kpi}
          {...(onOpenTti ? { onOpenTti } : {})}
          canManage={
            canManage ||
            (actorUserId !== null &&
              kpi.ownerUserId !== null &&
              actorUserId === kpi.ownerUserId &&
              kpi.lifecycleStatus !== "archived")
          }
        />

        <KpiConnectedWork kpiId={kpi.kpiId} />

        <KpiPerformanceSection
          kpi={kpi}
          canRecord={canRecord}
          canManage={canManage}
          range={range}
          onRangeChange={setRange}
        />

        <KpiActivitySection kpiId={kpi.kpiId} />

        <KpiPanel title="KPI details" headingId="kpi-details-heading">
          <p className="mb-4 text-body text-pb-text-secondary">
            {kpi.description || "No description yet."}
          </p>
          {/* Kept hand-built (not DescriptionList): its `columns` breakpoint
              is a viewport media query; this grid's 2→3 column step is a
              CONTAINER query (@2xl/content), which the part has no match for. */}
          <dl className="grid grid-cols-2 gap-x-4.5 gap-y-3.75 @2xl/content:grid-cols-3">
            <Field label="KPI Template">{templateLabel}</Field>
            <Field label="Category">{categoryLabel ?? NONE}</Field>
            <Field label="Unit">{kpi.unit || NONE}</Field>
            <Field label="Direction">{DIRECTION_LABEL[kpi.direction]}</Field>
            <Field label="Reporting frequency">
              {kpi.reportingFrequency
                ? REPORTING_FREQUENCY_LABEL[kpi.reportingFrequency]
                : NONE}
            </Field>
            <Field label="Owner">
              {kpi.ownerUserId ? (
                owner ? (
                  <span className="inline-flex items-center gap-1.5">
                    <OwnerChip
                      initials={initialsOf(owner.name)}
                      name={owner.name}
                    />
                    {owner.active ? null : (
                      <Badge shape="pill" size="sm" tone="watch" variant="soft">
                        Not active
                      </Badge>
                    )}
                  </span>
                ) : (
                  <span className="text-pb-text-muted">
                    {kpi.ownerUserId.slice(0, 8)}…
                  </span>
                )
              ) : (
                <span className="text-pb-text-muted">Unassigned</span>
              )}
            </Field>
            <Field label="Created">{formatDateTime(kpi.createdAt)}</Field>
            <Field label="Last updated">{formatDateTime(kpi.updatedAt)}</Field>
          </dl>
          {/* Measurement + performance only — the lifecycle pill stays in the
              header, so this quieter section adds facts instead of echoing it. */}
          <div className="mt-4 flex flex-wrap gap-2 border-t border-pb-border/50 pt-3.5">
            <KpiStatusBadges state={kpi.currentState} prefixed hint={false} />
          </div>
        </KpiPanel>
      </div>

      <AssignOwnerModal
        kpiId={kpi.kpiId}
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
      />
      <ConfirmDialog
        open={restoreOpen}
        onClose={() => setRestoreOpen(false)}
        onConfirm={handleRestore}
        tone="neutral"
        title={`Restore ${kpi.name}?`}
        confirmLabel="Restore"
        loading={restore.isPending}
      >
        This KPI will return to Draft. Its owner, target, and value history will
        be kept.
      </ConfirmDialog>
    </div>
  );
}
